import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Helmet } from "react-helmet";
import { makeStyles } from "@mui/styles";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {
  Card,
  TablePagination,
  Select,
  CardContent,
  Grid,
  Container,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Tooltip,
  IconButton,
  Skeleton,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import DevicesIcon from "@mui/icons-material/Devices";
import StorageIcon from "@mui/icons-material/Storage";
import HubIcon from "@mui/icons-material/Hub";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DynamicFormIcon from "@mui/icons-material/DynamicForm";
import moment from "moment";
import Error403 from "../Error/403";
import NewAssignUsers from "./NewAssignUsers";
import AssignUsersEdit from "./AssignUsersEdit";
import AssignUsersDelete from "./AssignUserDelete";
import { DataGrid } from "@mui/x-data-grid";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";

const useStyles = makeStyles(() => ({
  root: {
    flexGrow: 1,
  },
}));

const LAB_TYPES = {
  preconfigured: { label: "Preconfigured", key: "preconfig", endpoint: "/api/preConfigured/", icon: <FormatListBulletedIcon /> },
  custom: { label: "Custom", key: "custom", endpoint: "/api/manageInstance/", icon: <DynamicFormIcon /> },
  hypervised: { label: "Hypervised", key: "hvm", endpoint: "/api/virtualizationCustomlabs/", icon: <DevicesIcon /> },
  shared: { label: "Shared", key: "svm", endpoint: "/api/sharedvmlabs/", icon: <HubIcon /> },
  cloud: { label: "Cloud", key: "cloud", endpoint: "/api/manageCloudlabs/", icon: <CloudQueueIcon /> },
  wise: { label: "Wise", key: "wise", endpoint: "/api/wiselabs/", icon: <AutoAwesomeIcon /> },
  manual: { label: "Manual", key: "manual", endpoint: "/api/getmanuallabdetails/", icon: <StorageIcon /> },
  saas: { label: "SaaS", key: "saas", endpoint: "/api/getsaslabdetails/", icon: <CloudDoneIcon /> },
};

export default function Request() {
  const classes = useStyles();
  const [searchitems, setSearchItems] = useState("");
  const [labTypeView, setLabTypeView] = useState("preconfigured");
  const [labData, setLabData] = useState({});
  const [loading, setLoading] = useState({});

  const [pagination, setPagination] = useState(
    Object.keys(LAB_TYPES).reduce((acc, labType) => {
      acc[labType] = { page: 0, rowsPerPage: 10 };
      return acc;
    }, {})
  );

  const getCurrentPagination = () => pagination[labTypeView] || { page: 0, rowsPerPage: 10 };

  const handleChangePage = (event, newPage) => {
    setPagination((prev) => ({
      ...prev,
      [labTypeView]: {
        ...prev[labTypeView],
        page: newPage,
      },
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPagination((prev) => ({
      ...prev,
      [labTypeView]: {
        page: 0,
        rowsPerPage: newRowsPerPage,
      },
    }));
  };

  const handleLabTypeChange = (event, newLabType) => {
    if (!newLabType || newLabType === labTypeView) return;
    setLabTypeView(newLabType);
    setSearchItems("");
  };

  const fetchLabData = useCallback(
    async (labType) => {
      if (labData[labType] && labData[labType].length > 0) {
        return;
      }

      setLoading((prev) => ({ ...prev, [labType]: true }));

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}${LAB_TYPES[labType].endpoint}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (!data.detail) {
          setLabData((prev) => ({ ...prev, [labType]: data }));
        }
      } catch (error) {
        console.error(`Error fetching ${LAB_TYPES[labType]?.label} data:`, error);
        setLabData((prev) => ({ ...prev, [labType]: [] }));
      } finally {
        setLoading((prev) => ({ ...prev, [labType]: false }));
      }
    },
    [labData]
  );

  useEffect(() => {
    fetchLabData(labTypeView);
  }, [labTypeView, fetchLabData]);

  const getTableColumns = (labType) => [
    {
      field: labType === "cloud" ? "lab_name" : "name",
      headerName: "Name",
      flex: 1.2,
      minWidth: 160,
      headerAlign: "center",
      align: "left",
    },
    {
      field: "number_of_trainee",
      headerName: "Number of Trainee",
      flex: 1,
      minWidth: 140,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "created_at",
      headerName: "Created At",
      flex: 1,
      minWidth: 140,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (params.value ? moment(params.value).format("DD/MM/YYYY") : "--"),
    },
    {
      field: "action",
      headerName: "Assign users",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Tooltip title="Assign trainees">
          <span>
            <NewAssignUsers
              labtype={LAB_TYPES[labType]?.key}
              InstanceID={params.row.id}
              count={params.row.number_of_trainee}
              is_userassign={params.row.is_userassign}
            />
          </span>
        </Tooltip>
      ),
    },
    {
      field: "edit",
      headerName: "Edit",
      flex: 0.8,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Tooltip title="Edit assigned trainees">
          <span>
            <AssignUsersEdit
              labType={LAB_TYPES[labType]?.key}
              InstanceID={params.row.id}
              count={params.row.number_of_trainee}
              is_userassign={params.row.is_userassign}
            />
          </span>
        </Tooltip>
      ),
    },
    {
      field: "delete",
      headerName: "Delete",
      flex: 0.8,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Tooltip title="Remove assigned trainees">
          <span>
            <AssignUsersDelete
              labType={LAB_TYPES[labType]?.key}
              InstanceID={params.row.id}
              count={params.row.number_of_trainee}
              is_userassign={params.row.is_userassign}
            />
          </span>
        </Tooltip>
      ),
    },
  ];

  const filterLabList = (list, searchField = "name") => {
    if (!list || !Array.isArray(list)) return [];
    if (!searchitems) return list;

    return list.filter((instance) => {
      const fieldValue = instance[searchField]?.toString().toLowerCase() || "";
      return fieldValue.includes(searchitems.toString().toLowerCase());
    });
  };

  const getCurrentData = () => {
    const currentData = labData[labTypeView] || [];
    const { page, rowsPerPage } = getCurrentPagination();
    const searchField = labTypeView === "cloud" ? "lab_name" : "name";

    let filteredList = filterLabList(currentData, searchField);

    if (labTypeView === "preconfigured") {
      filteredList = filteredList.filter((item) => item.application !== "custom");
    } else if (labTypeView === "custom") {
      filteredList = filteredList.filter((item) => item.application === "custom");
    }

    const paginatedList = filteredList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return {
      fullList: currentData,
      filteredList,
      paginatedList,
    };
  };

  const { fullList, filteredList, paginatedList } = getCurrentData();
  const { page, rowsPerPage } = getCurrentPagination();
  const isLoading = loading[labTypeView];

  const summaryMetrics = useMemo(() => {
    const total = fullList.length;
    const assigned = fullList.filter((item) => item.is_userassign).length;
    const pending = total - assigned;
    const inView = filteredList.length;
    return { total, assigned, pending, inView };
  }, [fullList, filteredList]);

  const renderLoadingSkeleton = () => {
    const { rowsPerPage: currentRowsPerPage } = getCurrentPagination();
    const rowHeight = 52;
    const headerHeight = 52;
    const paginationHeight = 52;
    // Show skeleton for a reasonable number of rows (min 3, max rowsPerPage)
    const skeletonRowCount = Math.min(Math.max(3, currentRowsPerPage), currentRowsPerPage);
    const dataGridHeight = headerHeight + (skeletonRowCount * rowHeight);
    
    return (
      <Box sx={{ width: "100%" }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1,
          height: dataGridHeight
        }}>
          {/* Header skeleton */}
          <Box sx={{ display: 'flex', gap: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1, height: headerHeight }}>
            {[1, 2, 3, 4, 5, 6].map((col) => (
              <Skeleton key={`header-${col}`} variant="text" width="15%" height={32} />
            ))}
          </Box>
          {/* Row skeletons */}
          {Array.from({ length: skeletonRowCount }).map((_, index) => (
            <Box key={`row-${index}`} sx={{ display: 'flex', gap: 2, p: 1, height: rowHeight }}>
              {[1, 2, 3, 4, 5, 6].map((col) => (
                <Skeleton key={`cell-${index}-${col}`} variant="text" width="15%" height={40} />
              ))}
            </Box>
          ))}
        </Box>
        {/* Pagination skeleton */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', pl: 1, pt: 1, pb: 1, minHeight: paginationHeight }}>
          <Skeleton variant="text" width={200} height={48} />
        </Box>
      </Box>
    );
  };

  const renderEmptyState = () => {
    const minEmptyHeight = 200; // Minimum height for empty state
    
    return (
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            minHeight: minEmptyHeight,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            color: "text.secondary",
            py: 4,
          }}
        >
          <IconButton sx={{ mb: 1 }} disabled>
            <FormatListBulletedIcon fontSize="large" />
          </IconButton>
          <Typography variant="h6" gutterBottom>
            {fullList.length === 0 ? "No data available" : "No matching records found"}
          </Typography>
          <Typography variant="body2" align="center" sx={{ maxWidth: 360 }}>
            Try adjusting your filters or search keywords to find the lab you need.
          </Typography>
        </Box>
        {/* Pagination for empty state - still show it */}
        <Box sx={{ display: "flex", justifyContent: "flex-start", pl: 1, pt: 1, pb: 1, minHeight: 52 }}>
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={filteredList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              '& .MuiTablePagination-toolbar': {
                pl: 0,
                minHeight: 48,
              },
              '& .MuiTablePagination-spacer': {
                display: 'none',
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                mx: 0.5,
              },
            }}
          />
        </Box>
      </Box>
    );
  };

  const renderDataGrid = () => {
    if (isLoading) {
      return renderLoadingSkeleton();
    }

    if (paginatedList.length === 0) {
      return renderEmptyState();
    }

    // Calculate height based on actual rows displayed (not rowsPerPage)
    // This makes the table compact with no extra white space
    const rowHeight = 52;
    const headerHeight = 52;
    const paginationHeight = 52;
    const actualRowCount = paginatedList.length;
    const dataGridHeight = headerHeight + (actualRowCount * rowHeight);

    return (
      <Box sx={{ width: "100%", overflow: "visible" }}>
        <Box sx={{ width: "100%", overflow: "hidden", height: dataGridHeight }}>
          <DataGrid
            rows={paginatedList}
            getRowId={(row) => row.id}
            density="standard"
            autoHeight={false}
            disableSelectionOnClick
            columns={getTableColumns(labTypeView)}
            sx={{
              border: "none",
              height: dataGridHeight,
              width: '100%',
              // Remove scrollbars
              '& .MuiDataGrid-main': {
                overflow: 'hidden !important',
                width: '100%',
              },
              '& .MuiDataGrid-virtualScroller': {
                overflow: 'hidden !important',
                height: `${dataGridHeight - headerHeight}px !important`,
                width: '100%',
              },
              '& .MuiDataGrid-virtualScrollerContent': {
                overflow: 'hidden !important',
                width: '100%',
              },
              '& .MuiDataGrid-virtualScrollerRenderZone': {
                overflow: 'visible !important',
                width: '100%',
              },
              // Hide scrollbars
              '& .MuiDataGrid-scrollbar--vertical': {
                display: 'none !important',
              },
              '& .MuiDataGrid-scrollbar--horizontal': {
                display: 'none !important',
              },
              '& .MuiDataGrid-root': {
                overflow: 'hidden !important',
                width: '100%',
              },
              '& .MuiDataGrid-container--top': {
                overflow: 'hidden !important',
              },
              '& .MuiDataGrid-container--bottom': {
                overflow: 'hidden !important',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: (theme) => theme.palette.action.hover,
                borderBottom: '1px solid',
                borderColor: 'divider',
                fontWeight: 600,
              },
              '& .MuiDataGrid-row': {
                transition: 'background-color 0.2s ease',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: (theme) => theme.palette.action.hover,
              },
              '& .MuiDataGrid-cell': {
                borderColor: 'divider',
              },
            }}
            hideFooter
            disableVirtualization={false}
          />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-start", pl: 1, pt: 1, pb: 1, minHeight: paginationHeight }}>
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={filteredList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              '& .MuiTablePagination-toolbar': {
                pl: 0,
                minHeight: 48,
              },
              '& .MuiTablePagination-spacer': {
                display: 'none',
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                mx: 0.5,
              },
            }}
          />
        </Box>
      </Box>
    );
  };

  if (
    localStorage.getItem("user_access") ===
      "You do not have permission to perform this action." &&
    localStorage.getItem("group") !== "admin"
  ) {
    return <Error403 />;
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>LabAsService</title>
      </Helmet>

      <Container 
        maxWidth="xl" 
        className={classes.root} 
        sx={{ 
          py: { xs: 3, md: 4 },
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <Stack spacing={3}>
          <Card
            sx={{
              borderRadius: 3,
              p: { xs: 2.5, md: 3 },
              background: 'linear-gradient(135deg, rgba(25,118,210,0.12), rgba(46,125,50,0.08))',
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Typography variant="h4" fontWeight={600}>
                    Assign Trainees
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage trainee assignments across lab types, track progress, and keep your cohorts up to date.
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container spacing={2} justifyContent="flex-end">
                  <Grid item xs={12} sm={4} md={4}>
                    <Card elevation={0} sx={{ p: 2, borderRadius: 2, minHeight: 100 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Total labs
                      </Typography>
                      {isLoading && !labData[labTypeView] ? (
                        <Skeleton variant="text" width={60} height={40} />
                      ) : (
                        <Typography variant="h5" fontWeight={600}>
                          {summaryMetrics.total}
                        </Typography>
                      )}
                      {isLoading && !labData[labTypeView] ? (
                        <Skeleton variant="rounded" width={80} height={24} sx={{ mt: 0.5 }} />
                      ) : (
                        <Chip size="small" label={`${summaryMetrics.inView} in view`} sx={{ mt: 0.5 }} />
                      )}
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4} md={4}>
                    <Card elevation={0} sx={{ p: 2, borderRadius: 2, minHeight: 100 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Assigned
                      </Typography>
                      {isLoading && !labData[labTypeView] ? (
                        <Skeleton variant="text" width={60} height={40} />
                      ) : (
                        <Typography variant="h5" fontWeight={600} color="success.main">
                          {summaryMetrics.assigned}
                        </Typography>
                      )}
                      {isLoading && !labData[labTypeView] ? (
                        <Skeleton variant="rounded" width={80} height={24} sx={{ mt: 0.5 }} />
                      ) : (
                        <Chip size="small" color="success" label="Assigned" sx={{ mt: 0.5 }} />
                      )}
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4} md={4}>
                    <Card elevation={0} sx={{ p: 2, borderRadius: 2, minHeight: 100 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Pending
                      </Typography>
                      {isLoading && !labData[labTypeView] ? (
                        <Skeleton variant="text" width={60} height={40} />
                      ) : (
                        <Typography variant="h5" fontWeight={600} color="warning.main">
                          {summaryMetrics.pending}
                        </Typography>
                      )}
                      {isLoading && !labData[labTypeView] ? (
                        <Skeleton variant="rounded" width={120} height={24} sx={{ mt: 0.5 }} />
                      ) : (
                        <Chip size="small" color="warning" label="Needs attention" sx={{ mt: 0.5 }} />
                      )}
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Card>

          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* First Row: All Lab Type Labels */}
              <Box sx={{ width: '100%', overflowX: { xs: 'auto', md: 'visible' } }}>
                <ToggleButtonGroup
                  value={labTypeView}
                  exclusive
                  onChange={handleLabTypeChange}
                  size="small"
                  sx={{
                    flexWrap: 'nowrap',
                    display: 'flex',
                    width: { xs: 'max-content', md: '100%' },
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                      whiteSpace: 'nowrap',
                      flex: { xs: '0 0 auto', md: '1 1 auto' },
                    },
                  }}
                >
                  {Object.entries(LAB_TYPES).map(([value, config]) => (
                    <ToggleButton key={value} value={value}>
                      {config.icon}
                      {config.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              {/* Second Row: Search and Rows Selector */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <TextField
                  value={searchitems}
                  onChange={(event) => {
                    const { value } = event.target;
                    setSearchItems(value);
                    setPagination((prev) => ({
                      ...prev,
                      [labTypeView]: {
                        ...prev[labTypeView],
                        page: 0,
                      },
                    }));
                  }}
                  placeholder="Search lab name"
                  size="small"
                  fullWidth
                  sx={{ flex: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControl sx={{ minWidth: { xs: '100%', sm: 140 } }} size="small">
                  <Select
                    value={rowsPerPage}
                    onChange={handleChangeRowsPerPage}
                    displayEmpty
                    renderValue={(value) => `Rows: ${value}`}
                  >
                    {[10, 25, 50, 75, 100].map((option) => (
                      <MenuItem key={option} value={option}>
                        {option} rows
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <Box sx={{ position: 'relative' }}>{renderDataGrid()}</Box>
            </CardContent>
          </Card>
        </Stack>
      </Container>
    </React.Fragment>
  );
}